import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { StatusBadge } from '@/components/ui/status-badge'
import type { Project } from '../types/home.types'

interface ProjectsTableProps {
  projects: Project[]
}

export function ProjectsTable({ projects }: ProjectsTableProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  if (projects.length === 0) {
    return (
      <div className="text-muted-foreground p-8 text-center text-sm">{t('home.noResults')}</div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-xs">
        <thead>
          <tr className="bg-muted">
            <th className="text-muted-foreground px-3 py-2 text-left font-semibold">
              {t('project.company')}
            </th>
            <th className="text-muted-foreground px-3 py-2 text-center font-semibold">
              {t('project.taxId')}
            </th>
            <th className="text-muted-foreground px-3 py-2 text-center font-semibold">
              {t('project.year')}
            </th>
            <th className="text-muted-foreground px-3 py-2 text-center font-semibold">
              {t('project.status')}
            </th>
            <th className="text-muted-foreground px-3 py-2 text-center font-semibold">
              {t('project.auditor')}
            </th>
            <th className="text-muted-foreground px-3 py-2 text-center font-semibold">
              {t('project.report')}
            </th>
            <th className="text-muted-foreground px-3 py-2 text-center font-semibold">
              {t('project.created')}
            </th>
            <th className="text-muted-foreground px-3 py-2 text-center font-semibold">
              {t('project.updated')}
            </th>
            <th className="text-muted-foreground px-3 py-2 text-center font-semibold"></th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => (
            <tr
              key={project.id}
              className="border-border hover:bg-accent border-t transition-colors"
            >
              <td className="px-3 py-2">
                <button
                  onClick={() => void navigate(`/companies/${project.companyId}`)}
                  className="border-primary/40 text-primary hover:text-primary/80 border-b border-dashed font-semibold"
                >
                  {project.company}
                </button>
              </td>
              <td className="text-muted-foreground px-3 py-2 text-center font-mono text-[10px]">
                {project.taxId}
              </td>
              <td className="text-primary px-3 py-2 text-center font-bold">{project.year}</td>
              <td className="px-3 py-2 text-center">
                <StatusBadge status={project.status} size="sm" />
              </td>
              <td className="px-3 py-2 text-center">{project.auditor}</td>
              <td className="px-3 py-2 text-center">
                {project.reportStatus === 'ออกแล้ว' ? (
                  <span className="inline-flex rounded-full border border-[var(--badge-report-issued-border)] bg-[var(--badge-report-issued-bg)] px-2 py-0.5 text-[10px] font-bold text-[var(--badge-report-issued-text)]">
                    {t('reportStatus.issued')}
                  </span>
                ) : project.reportStatus === 'ร่าง' ? (
                  <span className="inline-flex rounded-full border border-[var(--badge-report-draft-border)] bg-[var(--badge-report-draft-bg)] px-2 py-0.5 text-[10px] font-bold text-[var(--badge-report-draft-text)]">
                    {t('reportStatus.draft')}
                  </span>
                ) : (
                  <span className="text-muted-foreground">{t('reportStatus.none')}</span>
                )}
              </td>
              <td className="text-muted-foreground px-3 py-2 text-center">{project.created}</td>
              <td className="text-muted-foreground px-3 py-2 text-center">{project.updated}</td>
              <td className="px-3 py-2 text-center">
                <button
                  onClick={() => void navigate(`/workspaces/${project.id}`)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-1 rounded px-3 py-1.5 text-[10px] font-bold transition-colors"
                >
                  {t('project.open')}
                  <ArrowRight className="h-3 w-3" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
